import { usePlaceToStayAccommodationsQuery, PlaceToStayAccommodationPlaceV2Fragment } from '@graphql/generated';
import { addError } from '@shared/utils/logger';
import { useMemo, useEffect } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';

export const useAccommodationData = (): Readonly<{
  accommodationsMap: ReadonlyMap<string, PlaceToStayAccommodationPlaceV2Fragment>;
  loading: boolean;
  error: Error | null;
}> => {
  const { eventInfo } = useEventInfo();

  const { data, loading, error } = usePlaceToStayAccommodationsQuery({
    variables: {
      eventId: eventInfo?.eventId || '',
    },
    skip: !eventInfo?.eventId,
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
  });

  const accommodationsMap = useMemo(() => {
    if (!data?.event?.accommodationPlacesForDisplayLocation) {
      return new Map<string, PlaceToStayAccommodationPlaceV2Fragment>();
    }

    const map = new Map<string, PlaceToStayAccommodationPlaceV2Fragment>();
    for (const acc of data.event.accommodationPlacesForDisplayLocation) {
      if (acc?.id) {
        map.set(acc.id, acc);
      }
    }
    return map;
  }, [data?.event?.accommodationPlacesForDisplayLocation]);

  useEffect(() => {
    if (error) {
      addError(error, {
        source: 'PlaceToStayComponent',
        context: 'useAccommodationData',
        errorMessage: error.message,
        eventId: eventInfo?.eventId,
      });
    }
  }, [error, eventInfo?.eventId]);

  return {
    accommodationsMap,
    loading,
    error: error ? new Error(error.message) : null,
  };
};
